/**
 * One switch for every piece of billing UI. Production shows none of it while
 * any plan in plans.ts is `draft`; the tables behind it may not even exist
 * yet, so nothing billing-related is queried either.
 *
 * For review, previews and the UI harness can turn it on explicitly with
 * `?billing=preview` (remembered for the tab); `?billing=off` turns it off.
 * The flag is ignored on production builds.
 */
import { PLANS, type Plan } from "@/lib/pricing/plans";

export const BILLING_PREVIEW_KEY = "zettl:billing-preview";

/** Billing is live once no plan is a draft (the live-mode switch, plan §13 step 9). */
export function billingLive(plans: readonly Plan[] = PLANS): boolean {
  return plans.length > 0 && plans.every((p) => !p.draft);
}

export interface GateEnv {
  /** `VITE_VERCEL_ENV`: "production", "preview", "development" or empty (local dev, harness). */
  vercelEnv: string;
  /** `window.location.search`. */
  search: string;
  /** sessionStorage, or null where it can't be used. */
  storage: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null;
}

/** Live, or explicitly previewed outside production. Remembers the flag from the URL. */
export function billingVisible(env: GateEnv, plans: readonly Plan[] = PLANS): boolean {
  if (billingLive(plans)) return true;
  if (env.vercelEnv === "production") return false;

  const flag = new URLSearchParams(env.search).get("billing");
  if (!env.storage) return flag === "preview";
  try {
    if (flag === "preview") env.storage.setItem(BILLING_PREVIEW_KEY, "1");
    if (flag === "off") env.storage.removeItem(BILLING_PREVIEW_KEY);
    return env.storage.getItem(BILLING_PREVIEW_KEY) === "1";
  } catch {
    return flag === "preview";
  }
}

function browserEnv(): GateEnv {
  let storage: GateEnv["storage"] = null;
  try {
    storage = typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    storage = null;
  }
  return {
    vercelEnv: import.meta.env.VITE_VERCEL_ENV ?? "",
    search: typeof window !== "undefined" ? window.location.search : "",
    storage,
  };
}

/** The gate for components: call it during render. */
export function isBillingVisible(): boolean {
  return billingVisible(browserEnv());
}
